import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from './api';
import { clearNativeToken, isNative } from './api';
import { cancelCallReminder, listenForCallReminders, scheduleCallReminder } from './callReminders';
import { loadBilling, purchaseCreditPackage, refreshBalance, restorePurchases } from './billing';
import Auth from './Auth';
import './App.css';

async function launchScheduledCall(call, navigate) {
  const response = await axios.post('/api/calls/web', {
    child_id: call.child_id,
    theme: call.theme,
    sub_theme: call.sub_theme,
    caller_type: call.caller_type
  });
  try {
    await axios.delete(`/api/calls/${call.id}`);
    await cancelCallReminder(call.id);
  } catch (error) {
    console.error('Scheduled call cleanup failed:', error);
  }
  navigate(`/call/${response.data.callId}`);
}

function App() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [pendingScheduledCallId, setPendingScheduledCallId] = useState(null);
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [creditBalance, setCreditBalance] = useState(null);
  const [creditPacks, setCreditPacks] = useState([]);
  const [packSizes, setPackSizes] = useState({});
  const [purchasesAvailable, setPurchasesAvailable] = useState(false);
  const [billingMessage, setBillingMessage] = useState('');
  const [purchaseBusy, setPurchaseBusy] = useState(false);

  const [children, setChildren] = useState([]);
  const [upcomingCalls, setUpcomingCalls] = useState([]);
  const [callLogs, setCallLogs] = useState([]);
  const [themes, setThemes] = useState(null);
  const [activeTab, setActiveTab] = useState('children');

  // Form states
  const [childForm, setChildForm] = useState({
    name: '',
    phone_number: '',
    parent_name: '',
    parent_phone: ''
  });

  const [callForm, setCallForm] = useState({
    child_id: '',
    theme: 'NICE',
    sub_theme: '',
    caller_type: 'SANTA',
    scheduled_time: ''
  });

  const [immediateCallForm, setImmediateCallForm] = useState({
    child_id: '',
    theme: 'NICE',
    sub_theme: '',
    caller_type: 'SANTA',
    madLibsData: null
  });

  const [madLibsInputs, setMadLibsInputs] = useState({
    achievement: '',
    specificDetail: '',
    location: '',
    rewardOrPraise: ''
  });

  const [isImmediateCallModalOpen, setIsImmediateCallModalOpen] = useState(false);
  const [callLinkModal, setCallLinkModal] = useState({ isOpen: false, link: '', childName: '' });

  // Real phone calls are off in v1; phone fields only show when the server enables them
  const [phoneCallsEnabled, setPhoneCallsEnabled] = useState(false);

  // Check authentication on mount
  useEffect(() => {
    checkAuth();
    axios.get('/api/config')
      .then((response) => setPhoneCallsEnabled(Boolean(response.data.phoneCallsEnabled)))
      .catch((error) => console.error('Error loading config:', error));
  }, []);

  useEffect(() => {
    if (!isNative) return undefined;
    let listener;
    let removed = false;
    listenForCallReminders(id => setPendingScheduledCallId(id)).then(handle => {
      if (removed) handle?.remove();
      else listener = handle;
    }).catch(console.error);
    return () => {
      removed = true;
      listener?.remove();
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !pendingScheduledCallId) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get('/api/calls/upcoming');
        const call = response.data.find(item => Number(item.id) === pendingScheduledCallId);
        if (!call || cancelled) return;
        await launchScheduledCall(call, navigate);
      } catch (error) {
        if (!cancelled) alert('Could not start the scheduled call. Open Upcoming Calls to try again.');
      } finally {
        if (!cancelled) setPendingScheduledCallId(null);
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, pendingScheduledCallId, navigate]);

  // Load data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadChildren();
      loadThemes();
      loadUpcomingCalls();
      loadCallLogs();
      loadBilling().then(billing => {
        setCreditBalance(billing.balance);
        setCreditPacks(billing.packages);
        setPackSizes(billing.packs || {});
        setPurchasesAvailable(billing.purchasesAvailable);
      }).catch(error => {
        console.error('Billing setup failed:', error);
        setBillingMessage('Credits could not be loaded. Please try again.');
      });
    }
  }, [isAuthenticated]);

  const checkAuth = async () => {
    try {
      const response = await axios.get('/api/auth/me');
      if (response.data.authenticated) {
        setIsAuthenticated(true);
        setUser(response.data.user);
      }
    } catch (error) {
      if (isNative && error.response?.status === 401) {
        await clearNativeToken();
      }
      console.log('Not authenticated');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogin = (userData) => {
    setIsAuthenticated(true);
    setUser(userData);
  };

  const handleLogout = async () => {
    try {
      await axios.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      await clearNativeToken();
      setIsAuthenticated(false);
      setUser(null);
      setChildren([]);
      setUpcomingCalls([]);
      setCallLogs([]);
      setCreditBalance(null);
      setCreditPacks([]);
    }
  };

  const handleDeleteAccount = async event => {
    event.preventDefault();
    setDeletingAccount(true);
    setDeleteError('');
    try {
      await axios.delete('/api/auth/account', { data: { password: deletePassword } });
      await clearNativeToken();
      setDeleteAccountOpen(false);
      setDeletePassword('');
      setUser(null);
      setIsAuthenticated(false);
      setChildren([]);
      setUpcomingCalls([]);
      setCallLogs([]);
      setCreditBalance(null);
      setCreditPacks([]);
    } catch (error) {
      setDeleteError(error.response?.data?.error || 'Could not delete account. Please try again.');
    } finally {
      setDeletingAccount(false);
    }
  };

  const handlePurchase = async aPackage => {
    setPurchaseBusy(true);
    setBillingMessage('');
    try {
      await purchaseCreditPackage(aPackage);
      setBillingMessage('Purchase complete. Credits will appear when the store confirms it.');
      setTimeout(async () => {
        try { setCreditBalance(await refreshBalance()); }
        catch (error) { console.error('Could not refresh balance:', error); }
      }, 3000);
    } catch (error) {
      if (!error.userCancelled) setBillingMessage('Purchase could not be completed. Please try again.');
    } finally {
      setPurchaseBusy(false);
    }
  };

  const handleRestore = async () => {
    setPurchaseBusy(true);
    try {
      await restorePurchases();
      setCreditBalance(await refreshBalance());
      setBillingMessage('Purchases checked. Your call balance is shown above.');
    } catch (error) {
      setBillingMessage('Could not check purchases right now. Please try again.');
    } finally {
      setPurchaseBusy(false);
    }
  };

  const loadChildren = async () => {
    try {
      const response = await axios.get('/api/children');
      setChildren(response.data);
    } catch (error) {
      console.error('Error loading children:', error);
    }
  };

  const loadThemes = async () => {
    try {
      const response = await axios.get('/api/themes');
      setThemes(response.data);
    } catch (error) {
      console.error('Error loading themes:', error);
    }
  };

  const loadUpcomingCalls = async () => {
    try {
      const response = await axios.get('/api/calls/upcoming');
      setUpcomingCalls(response.data);
    } catch (error) {
      console.error('Error loading upcoming calls:', error);
    }
  };

  const loadCallLogs = async () => {
    try {
      const response = await axios.get('/api/logs');
      setCallLogs(response.data);
    } catch (error) {
      console.error('Error loading call logs:', error);
    }
  };

  const handleAddChild = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/children', childForm);
      setChildForm({ name: '', phone_number: '', parent_name: '', parent_phone: '' });
      loadChildren();
      alert('Child added successfully!');
    } catch (error) {
      alert('Error adding child: ' + error.message);
    }
  };

  const handleDeleteChild = async (id) => {
    if (window.confirm('Are you sure you want to delete this child?')) {
      try {
        await axios.delete(`/api/children/${id}`);
        loadChildren();
      } catch (error) {
        alert('Error deleting child: ' + error.message);
      }
    }
  };

  const handleScheduleCall = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post('/api/calls/schedule', callForm);
      let reminderSet = false;
      if (isNative) {
        try {
          reminderSet = await scheduleCallReminder(response.data.id, callForm.scheduled_time);
        } catch (error) {
          console.error('Could not schedule reminder:', error);
        }
      }
      setCallForm({
        child_id: '',
        theme: 'NICE',
        sub_theme: '',
        caller_type: 'SANTA',
        scheduled_time: ''
      });
      loadUpcomingCalls();
      alert(isNative
        ? reminderSet ? 'Call and device reminder scheduled!' : 'Call saved. Enable notifications to get a reminder.'
        : 'Call scheduled successfully!');
    } catch (error) {
      alert('Error scheduling call: ' + error.message);
    }
  };

  const openImmediateCallModal = (childId) => {
    setImmediateCallForm({ ...immediateCallForm, child_id: childId });
    setIsImmediateCallModalOpen(true);
  };

  const closeImmediateCallModal = () => {
    setIsImmediateCallModalOpen(false);
    setImmediateCallForm({
      child_id: '',
      theme: 'NICE',
      sub_theme: '',
      caller_type: 'SANTA',
      madLibsData: null
    });
    setMadLibsInputs({
      achievement: '',
      specificDetail: '',
      location: '',
      rewardOrPraise: ''
    });
  };

  const handleImmediateCallSubmit = async (e) => {
    e.preventDefault();
    try {
      // Include mad libs data if in MAD_LIBS mode
      const formData = { ...immediateCallForm };
      if (immediateCallForm.theme === 'MAD_LIBS') {
        formData.madLibsData = madLibsInputs;
      }

      const response = await axios.post('/api/calls/web', formData);
      console.log('Call created:', response.data);
      refreshBalance().then(setCreditBalance).catch(console.error);

      // Show the call link modal
      setCallLinkModal({
        isOpen: true,
        link: response.data.callLink,
        childName: response.data.childName
      });

      closeImmediateCallModal();
      loadCallLogs();
    } catch (error) {
      if (error.response?.status === 402) {
        setActiveTab('credits');
        alert('No call credits remaining.');
      } else {
        alert('Error creating call: ' + (error.response?.data?.error || error.message));
      }
    }
  };

  const handleImmediateCall = async (childId) => {
    openImmediateCallModal(childId);
  };

  const getSubThemes = (theme) => {
    if (!themes || !theme) return [];
    return Object.entries(themes[theme].subThemes).map(([key, value]) => ({
      key,
      name: value.name
    }));
  };

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div className="App" style={{display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh'}}>
        <div style={{textAlign: 'center'}}>
          <h2>🎅 Loading...</h2>
        </div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <Auth onLogin={handleLogin} />;
  }

  return (
    <div className="App">
      <header className="header">
        <div className="header-content">
          <div>
            <h1>🎅 North Pole Line 🎄</h1>
            <p>Spread Christmas cheer one call at a time!</p>
          </div>
          <div className="header-user">
            <span className="user-name">👤 {user?.name}</span>
            {creditBalance !== null && <span>{creditBalance} call credit{creditBalance === 1 ? '' : 's'}</span>}
            <button onClick={() => setDeleteAccountOpen(true)} className="logout-btn">Delete Account</button>
            <button onClick={handleLogout} className="logout-btn">Logout</button>
          </div>
        </div>
      </header>

      {deleteAccountOpen && (
        <div className="modal-overlay">
          <div className="modal" role="dialog" aria-modal="true" aria-labelledby="delete-account-title">
            <h2 id="delete-account-title">Delete your account</h2>
            <p>This permanently removes your profile, children, calls, saved call media, and unused call credits. Store purchase records remain with Apple or Google; consumable credits cannot be restored after deletion.</p>
            <form onSubmit={handleDeleteAccount} className="form">
              <label htmlFor="delete-account-password">Enter your password to confirm</label>
              <input
                id="delete-account-password"
                type="password"
                autoComplete="current-password"
                value={deletePassword}
                onChange={event => setDeletePassword(event.target.value)}
                required
              />
              {deleteError && <p role="alert">{deleteError}</p>}
              <div className="modal-actions">
                <button type="submit" disabled={deletingAccount}>Delete Account</button>
                <button type="button" onClick={() => {
                  setDeleteAccountOpen(false);
                  setDeletePassword('');
                  setDeleteError('');
                }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isImmediateCallModalOpen && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Create Call</h2>
            <form onSubmit={handleImmediateCallSubmit} className="form">
              <select
                value={immediateCallForm.theme}
                onChange={(e) => {
                  const newTheme = e.target.value;
                  setImmediateCallForm({
                    ...immediateCallForm,
                    theme: newTheme,
                    sub_theme: newTheme === 'MAD_LIBS' ? 'CUSTOM' : ''
                  });
                  // Reset mad libs inputs when switching themes
                  if (newTheme !== 'MAD_LIBS') {
                    setMadLibsInputs({
                      achievement: '',
                      specificDetail: '',
                      location: '',
                      rewardOrPraise: ''
                    });
                  }
                }}
                required
              >
                <option value="NICE">✨ Nice List</option>
                <option value="NAUGHTY">⚠️ Needs Improvement</option>
                <option value="MAD_LIBS">🎨 Custom Message (Mad Libs)</option>
              </select>

              {immediateCallForm.theme !== 'MAD_LIBS' ? (
                <select
                  value={immediateCallForm.sub_theme}
                  onChange={(e) => setImmediateCallForm({...immediateCallForm, sub_theme: e.target.value})}
                  required
                >
                  <option value="">Select Reason</option>
                  {getSubThemes(immediateCallForm.theme).map(theme => (
                    <option key={theme.key} value={theme.key}>{theme.name}</option>
                  ))}
                </select>
              ) : (
                <div className="mad-libs-inputs">
                  <div className="input-group">
                    <label htmlFor="achievement">What did they do well? *</label>
                    <input
                      id="achievement"
                      type="text"
                      placeholder="e.g., 'cleaned your room without being asked'"
                      value={madLibsInputs.achievement}
                      onChange={(e) => setMadLibsInputs({...madLibsInputs, achievement: e.target.value})}
                      required
                    />
                    <small>Example: "you helped your sister with her homework"</small>
                  </div>

                  <div className="input-group">
                    <label htmlFor="specificDetail">Add a specific detail</label>
                    <input
                      id="specificDetail"
                      type="text"
                      placeholder="e.g., 'You even organized your toys!'"
                      value={madLibsInputs.specificDetail}
                      onChange={(e) => setMadLibsInputs({...madLibsInputs, specificDetail: e.target.value})}
                    />
                    <small>Optional: Add more context about what they did</small>
                  </div>

                  <div className="input-group">
                    <label htmlFor="location">Where or when?</label>
                    <input
                      id="location"
                      type="text"
                      placeholder="e.g., 'at school' or 'yesterday'"
                      value={madLibsInputs.location}
                      onChange={(e) => setMadLibsInputs({...madLibsInputs, location: e.target.value})}
                    />
                    <small>Optional: Where or when did this happen?</small>
                  </div>

                  <div className="input-group">
                    <label htmlFor="rewardOrPraise">Special message or reward</label>
                    <input
                      id="rewardOrPraise"
                      type="text"
                      placeholder="e.g., 'I'm adding extra presents for you!'"
                      value={madLibsInputs.rewardOrPraise}
                      onChange={(e) => setMadLibsInputs({...madLibsInputs, rewardOrPraise: e.target.value})}
                    />
                    <small>Optional: Custom praise or reward message</small>
                  </div>
                </div>
              )}

              <select
                value={immediateCallForm.caller_type}
                onChange={(e) => setImmediateCallForm({...immediateCallForm, caller_type: e.target.value})}
                required
              >
                <option value="SANTA">🎅 Santa Claus</option>
                <option value="ELF_MALE">🧝‍♂️ Jingle Jack (Male Elf)</option>
                <option value="ELF_FEMALE">🧝‍♀️ Snowdrop Ellie (Female Elf)</option>
                <option value="MRS_CLAUS">👵 Mrs. Claus</option>
                <option value="GRANDPA_JOE">🎅 Santa Option 2</option>
              </select>

              <div className="modal-actions">
                <button type="submit">{isNative ? 'Prepare Call' : 'Generate Call Link'}</button>
                <button type="button" onClick={closeImmediateCallModal}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {callLinkModal.isOpen && (
        <div className="modal-overlay">
          <div className="modal call-link-modal">
            <h2>{isNative ? '🎅 Call Ready!' : '🎅 Call Link Ready!'}</h2>
            <p className="modal-subtitle">
              {isNative ? `Hand the device to ${callLinkModal.childName}, then start the call.` : `Share this link with ${callLinkModal.childName} at the perfect moment!`}
            </p>

            {!isNative && <>
            <div className="call-link-container">
              <input
                type="text"
                value={callLinkModal.link}
                readOnly
                className="call-link-input"
                onClick={(e) => e.target.select()}
              />
              <button
                className="copy-btn"
                onClick={() => {
                  // Use clipboard API if available, otherwise fallback for HTTP
                  if (navigator.clipboard && navigator.clipboard.writeText) {
                    navigator.clipboard.writeText(callLinkModal.link);
                    alert('Link copied to clipboard!');
                  } else {
                    // Fallback for HTTP - use textarea and execCommand
                    const textarea = document.createElement('textarea');
                    textarea.value = callLinkModal.link;
                    textarea.style.position = 'fixed';
                    textarea.style.opacity = '0';
                    document.body.appendChild(textarea);
                    textarea.select();
                    try {
                      document.execCommand('copy');
                      alert('Link copied to clipboard!');
                    } catch (err) {
                      alert('Copy failed. Please select and copy manually.');
                    }
                    document.body.removeChild(textarea);
                  }
                }}
              >
                📋 Copy Link
              </button>
            </div>

            <div className="modal-tips">
              <h4>💡 Tips:</h4>
              <ul>
                <li>Send via text, email, or open directly</li>
                <li>Works on any device with a browser</li>
                <li>Child will see an incoming call screen</li>
                <li>Link expires in 7 days</li>
              </ul>
            </div>
            </>}

            <div className="modal-actions">
              <button
                onClick={() => {
                  if (isNative) {
                    navigate(new URL(callLinkModal.link).pathname);
                  } else {
                    window.open(callLinkModal.link, '_blank');
                  }
                }}
                className="btn-test"
              >
                {isNative ? 'Start Call' : '🧪 Test Call'}
              </button>
              <button
                onClick={() => setCallLinkModal({ isOpen: false, link: '', childName: '' })}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <nav className="tabs">
        <button
          className={activeTab === 'credits' ? 'active' : ''}
          onClick={() => setActiveTab('credits')}
        >
          🎁 Call Credits
        </button>
        <button 
          className={activeTab === 'children' ? 'active' : ''}
          onClick={() => setActiveTab('children')}
        >
          👶 Children
        </button>
        <button 
          className={activeTab === 'schedule' ? 'active' : ''}
          onClick={() => setActiveTab('schedule')}
        >
          📅 Schedule Calls
        </button>
        <button 
          className={activeTab === 'upcoming' ? 'active' : ''}
          onClick={() => setActiveTab('upcoming')}
        >
          ⏰ Upcoming Calls
        </button>
        <button 
          className={activeTab === 'logs' ? 'active' : ''}
          onClick={() => setActiveTab('logs')}
        >
          📞 Call History
        </button>
      </nav>

      <main className="content">
        {activeTab === 'credits' && (
          <div className="section">
            <h2>Call Credits</h2>
            <p>You have {creditBalance === null ? '…' : creditBalance} call credits. Each call uses one credit.</p>
            <button onClick={async () => {
              try { setCreditBalance(await refreshBalance()); }
              catch (error) { setBillingMessage('Could not refresh your balance.'); }
            }}>Refresh Balance</button>
            {isNative && purchasesAvailable && (
              <>
                <h3>Get more calls</h3>
                {creditPacks.map(aPackage => (
                  <button
                    key={aPackage.identifier}
                    disabled={purchaseBusy}
                    onClick={() => handlePurchase(aPackage)}
                  >
                    {packSizes[aPackage.product.identifier]} calls · {aPackage.product.priceString}
                  </button>
                ))}
                <button disabled={purchaseBusy} onClick={handleRestore}>Restore Purchases</button>
              </>
            )}
            {isNative && !purchasesAvailable && <p>More call credits are not available yet.</p>}
            {billingMessage && <p role="status">{billingMessage}</p>}
          </div>
        )}
        {/* CHILDREN TAB */}
        {activeTab === 'children' && (
          <div className="section">
            <h2>Manage Children</h2>
            
            <form onSubmit={handleAddChild} className="form">
              <h3>Add New Child</h3>
              <input
                type="text"
                placeholder="Child's Name"
                value={childForm.name}
                onChange={(e) => setChildForm({...childForm, name: e.target.value})}
                required
              />
              {phoneCallsEnabled && (
                <>
                  <input
                    type="tel"
                    placeholder="Phone Number"
                    value={childForm.phone_number}
                    onChange={(e) => setChildForm({...childForm, phone_number: e.target.value})}
                    required
                  />
                  <input
                    type="text"
                    placeholder="Parent's Name"
                    value={childForm.parent_name}
                    onChange={(e) => setChildForm({...childForm, parent_name: e.target.value})}
                  />
                  <input
                    type="tel"
                    placeholder="Parent's Phone"
                    value={childForm.parent_phone}
                    onChange={(e) => setChildForm({...childForm, parent_phone: e.target.value})}
                  />
                </>
              )}
              <button type="submit">Add Child</button>
            </form>

            <div className="list">
              <h3>Children List</h3>
              {children.length === 0 ? (
                <p className="empty">No children added yet. Add your first child above!</p>
              ) : (
                <div className="cards">
                  {children.map(child => (
                    <div key={child.id} className="card">
                      <h4>{child.name}</h4>
                      {phoneCallsEnabled && child.phone_number && <p>📞 {child.phone_number}</p>}
                      {child.parent_name && <p>👤 {child.parent_name}</p>}
                      <div className="card-actions">
                        <button
                          onClick={() => handleImmediateCall(child.id)}
                          className="btn-call"
                        >
                          {isNative ? '🎅 Prepare Call' : '🎅 Create Call Link'}
                        </button>
                        <button 
                          onClick={() => handleDeleteChild(child.id)}
                          className="btn-delete"
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* SCHEDULE CALLS TAB */}
        {activeTab === 'schedule' && (
          <div className="section">
            <h2>Schedule a Call</h2>
            
            <form onSubmit={handleScheduleCall} className="form">
              <select
                value={callForm.child_id}
                onChange={(e) => setCallForm({...callForm, child_id: e.target.value})}
                required
              >
                <option value="">Select Child</option>
                {children.map(child => (
                  <option key={child.id} value={child.id}>{child.name}</option>
                ))}
              </select>

              <select
                value={callForm.theme}
                onChange={(e) => setCallForm({...callForm, theme: e.target.value, sub_theme: ''})}
                required
              >
                <option value="NICE">✨ Nice List</option>
                <option value="NAUGHTY">⚠️ Needs Improvement</option>
              </select>

              <select
                value={callForm.sub_theme}
                onChange={(e) => setCallForm({...callForm, sub_theme: e.target.value})}
                required
              >
                <option value="">Select Reason</option>
                {getSubThemes(callForm.theme).map(theme => (
                  <option key={theme.key} value={theme.key}>{theme.name}</option>
                ))}
              </select>

              <select
                value={callForm.caller_type}
                onChange={(e) => setCallForm({...callForm, caller_type: e.target.value})}
                required
              >
                <option value="SANTA">🎅 Santa Claus</option>
                <option value="ELF_MALE">🧝‍♂️ Jingle Jack (Male Elf)</option>
                <option value="ELF_FEMALE">🧝‍♀️ Snowdrop Ellie (Female Elf)</option>
                <option value="MRS_CLAUS">👵 Mrs. Claus</option>
                <option value="GRANDPA_JOE">🎅 Santa Option 2</option>
              </select>

              <input
                type="datetime-local"
                value={callForm.scheduled_time}
                onChange={(e) => setCallForm({...callForm, scheduled_time: e.target.value})}
                required
              />

              <button type="submit">Schedule Call</button>
            </form>
          </div>
        )}

        {/* UPCOMING CALLS TAB */}
        {activeTab === 'upcoming' && (
          <div className="section">
            <h2>Upcoming Scheduled Calls</h2>
            {upcomingCalls.length === 0 ? (
              <p className="empty">No upcoming calls scheduled.</p>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Child</th>
                      <th>Theme</th>
                      <th>Caller</th>
                      <th>Scheduled Time</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {upcomingCalls.map(call => (
                      <tr key={call.id}>
                        <td>{call.name}</td>
                        <td>{call.theme} - {call.sub_theme}</td>
                        <td>{call.caller_type}</td>
                        <td>{new Date(call.scheduled_time).toLocaleString()}</td>
                        <td><span className={`status ${call.status}`}>{call.status}</span></td>
                        <td>
                          <button
                            onClick={() => launchScheduledCall(call, navigate).catch(() => alert('Could not start call. Please try again.'))}
                            className="btn-call"
                          >Start Call</button>
                          <button 
                            onClick={async () => {
                              if (window.confirm('Delete this scheduled call?')) {
                                await axios.delete(`/api/calls/${call.id}`);
                                await cancelCallReminder(call.id);
                                loadUpcomingCalls();
                              }
                            }}
                            className="btn-delete-small"
                          >
                            Cancel
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* CALL LOGS TAB */}
        {activeTab === 'logs' && (
          <div className="section">
            <h2>Call History</h2>
            {callLogs.length === 0 ? (
              <p className="empty">No calls made yet.</p>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Child</th>
                      <th>Theme</th>
                      <th>Caller</th>
                      <th>Duration</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {callLogs.map(log => {
                      const isExpired = log.link_expiration && new Date(log.link_expiration) < new Date();
                      const hasLink = log.web_call_link && !isExpired;

                      return (
                        <tr key={log.id}>
                          <td>{new Date(log.created_at).toLocaleString()}</td>
                          <td>{log.child_name}</td>
                          <td>{log.theme} - {log.sub_theme}</td>
                          <td>{log.caller_type}</td>
                          <td>{log.duration ? `${log.duration}s` : '-'}</td>
                          <td><span className={`status ${log.status}`}>{log.status || 'completed'}</span></td>
                          <td>
                            {hasLink ? (
                              <button
                                className="btn-call-small"
                                onClick={() => window.open(log.web_call_link, '_blank')}
                                title="Replay this call"
                              >
                                🔄 Replay
                              </button>
                            ) : (
                              isExpired ? (
                                <span className="expired-text" style={{color: '#999', fontSize: '0.85em'}}>Expired</span>
                              ) : (
                                <span style={{color: '#999'}}>-</span>
                              )
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </main>

      <footer className="footer">
        <p>Made with ❤️ and Christmas magic 🎄</p>
      </footer>
    </div>
  );
}

export default App;
